import React, {useState, useRef} from 'react'
import PropTypes from 'prop-types'
import {
    Box,
    useDisclosure,
    Link as NavLink
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {useProducts} from '@salesforce/commerce-sdk-react'
import Title from '../shop-the-look/partials/title'
import Description from '../shop-the-look/partials/description'
import CloudinaryImage from '../../../cloudinary-media/image'
import Video from '../hero/partials/video'
import ShopThisLookModal from '../shop-the-look/partials/modal'
import {useGlobaleContext} from '../../../../contexts/globale'
import {SELECTED_ATTRIBUTES_WIDGET_PRODUCTS} from '../../../../constants'
import {Link as RouteLink} from 'react-router-dom'

const SHOP_THE_LOOK_CLOUDINARY_ASPECT_RATIO = '500:574'

export const ShopTheLookGalleryItem = ({
    title,
    titleTag,
    titleColor,
    titleSizeLargeDesktop,
    titleSizeMobile,
    titleSizeSmallDesktop,
    titleSizeTablet,
    image,
    imageAlt,
    titleFontFamily,
    titleAlign,
    description,
    descriptionTag,
    descriptionFontFamily,
    descriptionColor = 'black',
    descriptionAlign,
    btnText,
    btnFontFamily,
    btnAlign,
    btnColor,
    btnBgColor,
    ctaType = 'button',
    redirectionUrl = '',
    mediaType,
    isOverCopyLocation,
    desktopVideo,
    mobileVideo,
    desktopVideoThumbnail,
    mobileVideoThumbnail,
    videoAlt,
    videoEnableSound,
    videoDisableAutoPlay,
    clickBehavior,
    ...rest
}) => {
    const selectedAttributes = SELECTED_ATTRIBUTES_WIDGET_PRODUCTS
    const modalDisclosure = useDisclosure()
    const [loadProducts, setLoadProducts] = useState(false)
    const {countryISO, currencyCode} = useGlobaleContext()
    const textRef = useRef(null)

    const productIds = []
    let index = 1
    const uniqueProductIds = new Set()

    while (`product${index}` in rest) {
        uniqueProductIds.add(rest[`product${index}`])
        delete rest[`product${index}`]
        index++
    }

    const analyticsProps = {
        component_id: rest.variant || rest.typeId,
        component_name: title || rest.name || 'ShopTheLookGalleryItem'
    }

    productIds.push(...uniqueProductIds)

    const {data: products} = useProducts(
        {
            parameters: {
                ids: productIds.join(','),
                expand: ['availability', 'links', 'promotions', 'images', 'prices', 'variations'],
                perPricebook: true,
                select: selectedAttributes,
                allVariationProperties: true,
                allImages: true,
                c_selectedCountry: countryISO,
                c_selectedCurrency: currencyCode
            }
        },
        {
            enabled: Boolean(productIds.length) && loadProducts,
            keepPreviousData: true
        }
    )

    const handleClick = (e) => {
        // If the click originated from a nested control (e.g. video play/pause button), do not open the flyout.
        if (e && e.target && e.currentTarget) {
            try {
                const nestedButton = e.target.closest && e.target.closest('button')
                if (nestedButton && nestedButton !== e.currentTarget) {
                    return
                }
            } catch (err) {
                console.warn('Shop the Look flyout:', err.message)
            }
        }

        if (clickBehavior === 'shopTheLookFlyout') {
            setLoadProducts(true)
            modalDisclosure.onOpen()
        }
    }

    const hexToRgba = (hex, alpha = 1) => {
        const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i
        hex = hex?.replace(shorthandRegex, (_, r, g, b) => r + r + g + g + b + b)

        const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
        return result
            ? `rgba(${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(
                  result[3],
                  16
              )}, ${alpha})`
            : hex
    }

    let redirectionProps = {}
    let clickHandler = undefined

    // Flyout behavior
    if (clickBehavior === 'shopTheLookFlyout') {
        clickHandler = handleClick
    }

    // Standard link behavior (open in new tab)
    if (redirectionUrl && clickBehavior === 'standardLink') {
        const isExternal = /^https?:\/\//.test(redirectionUrl)

        redirectionProps = isExternal
            ? {
                  as: NavLink,
                  href: redirectionUrl,
                  target: '_blank',
                  rel: 'noopener noreferrer'
              }
            : {
                  as: RouteLink,
                  to: redirectionUrl,
                  target: '_blank'
              }
    }

    // Disable everything if noHyperLink
    const isNoHyperLink = clickBehavior === 'noHyperLink'

    return (
        <Box className="gallery-wrapper" display="flex">
            <Box
                ref={textRef}
                className="gallery-item-button"
                onClick={clickHandler}
                pointerEvents={isNoHyperLink ? 'none' : 'auto'}
                cursor={isNoHyperLink ? 'default' : 'pointer'}
                variant="unstyled"
                position="relative"
                width="100%"
                height="auto"
                maxWidth="auto"
                maxHeight="auto"
                aspectRatio="344:416"
                whiteSpace="normal"
                {...redirectionProps}
            >
                {mediaType === 'image' && (
                    <CloudinaryImage
                        src={image}
                        alt={imageAlt || title || ''}
                        aspectRatio="344:416"
                        imgSizes={{base: '100vw', md: '37vw', lg: '37vw'}}
                        sizes={[375, 390, 414, 640]}
                        styles={{
                            aspectRatio: '344 / 416',
                            width: '100%',
                            height: 'auto',
                            htmlWidth: 344,
                            htmlHeight: 416
                        }}
                    />
                )}

                {mediaType === 'video' && (
                    <Video
                        desktopVideo={desktopVideo}
                        mobileVideo={mobileVideo || desktopVideo}
                        desktopVideoThumbnail={desktopVideoThumbnail}
                        mobileVideoThumbnail={mobileVideoThumbnail}
                        videoDisableAutoPlay={videoDisableAutoPlay}
                        videoEnableSound={videoEnableSound}
                        videoAlt={videoAlt}
                        variant="hero"
                        desktopMediaPosition="full"
                        mobileMediaPosition="full"
                        cloudinaryAspectRatio={SHOP_THE_LOOK_CLOUDINARY_ASPECT_RATIO}
                    />
                )}

                <Box
                    position="absolute"
                    left={{base: '12px', sm: '22px', lg: '20px'}}
                    bottom={{base: '17px', sm: '20px', md: '19px', lg: '30px'}}
                    width="calc(100% - 40px)"
                    className="gallery-item-headline"
                >
                    {isOverCopyLocation && (
                        <Box className="gallery-item-title-above">
                            {title && (
                                <Title
                                    title={title}
                                    titleColor={titleColor}
                                    titleSizeLargeDesktop={titleSizeLargeDesktop}
                                    titleSizeMobile={titleSizeMobile}
                                    titleFontFamily={titleFontFamily}
                                    titleSizeSmallDesktop={titleSizeSmallDesktop}
                                    titleSizeTablet={titleSizeTablet}
                                    titleTag={titleTag}
                                    titleAlign={titleAlign}
                                />
                            )}
                            {description && (
                                <Description
                                    description={description}
                                    descriptionTag={descriptionTag}
                                    descriptionColor={descriptionColor}
                                    descriptionFontFamily={descriptionFontFamily}
                                    descriptionAlign={descriptionAlign}
                                    marginBottom="20px"
                                />
                            )}
                        </Box>
                    )}

                    {btnText && (
                        <Box
                            width="100%"
                            display="flex"
                            justifyContent={btnAlign === 'center' ? 'center' : ''}
                            className="gallery-item-btn-type"
                        >
                            {ctaType === 'button' ? (
                                <Box
                                    background={hexToRgba(btnBgColor, 0.5)}
                                    _hover={{
                                        background: hexToRgba(btnBgColor, 1),
                                        textDecoration: 'none'
                                    }}
                                    color={btnColor}
                                    fontFamily={btnFontFamily}
                                    borderRadius="22px"
                                    paddingX="25px"
                                    paddingY="4px"
                                >
                                    {btnText}
                                </Box>
                            ) : (
                                <Box
                                    textDecoration="underline"
                                    textUnderlineOffset="4px"
                                    color={btnColor}
                                    fontFamily={btnFontFamily}
                                >
                                    {btnText}
                                </Box>
                            )}
                        </Box>
                    )}
                </Box>

                {clickBehavior === 'shopTheLookFlyout' && (
                    <ShopThisLookModal
                        desktopImage={image}
                        mobileImage={image}
                        products={products}
                        modalDisclosure={modalDisclosure}
                        analyticsProps={analyticsProps}
                    />
                )}
            </Box>
        </Box>
    )
}

ShopTheLookGalleryItem.propTypes = {
    title: PropTypes.string,
    titleTag: PropTypes.string,
    titleFontFamily: PropTypes.string,
    titleColor: PropTypes.string,
    titleSizeLargeDesktop: PropTypes.string,
    titleSizeMobile: PropTypes.string,
    titleSizeSmallDesktop: PropTypes.string,
    titleSizeTablet: PropTypes.string,
    titleAlign: PropTypes.string,
    description: PropTypes.string,
    descriptionTag: PropTypes.string,
    descriptionFontFamily: PropTypes.string,
    descriptionColor: PropTypes.string,
    descriptionAlign: PropTypes.string,
    btnText: PropTypes.string,
    btnFontFamily: PropTypes.string,
    btnAlign: PropTypes.string,
    btnColor: PropTypes.string,
    btnBgColor: PropTypes.string,
    image: PropTypes.string,
    imageAlt: PropTypes.string,
    redirectionUrl: PropTypes.string,
    ctaType: PropTypes.string,
    mediaType: PropTypes.string,
    isOverCopyLocation: PropTypes.bool,
    desktopVideo: PropTypes.string,
    mobileVideo: PropTypes.string,
    desktopVideoThumbnail: PropTypes.string,
    mobileVideoThumbnail: PropTypes.string,
    videoAlt: PropTypes.string,
    videoEnableSound: PropTypes.bool,
    videoDisableAutoPlay: PropTypes.bool,
    clickBehavior: PropTypes.string
}

export default ShopTheLookGalleryItem
